import { useEffect, useMemo, useState } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Hash, Loader2, Search, Trash2, X } from "@fg/icons";
import { connectApi, connectParityApi, normalizeUserId, type Channel, type ChannelMember, type Team } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";
import { ChatAvatar } from "../chat/chatUi";

type Tab = "people" | "channels" | "settings";

/**
 * Manage a team, as in Teams: its people (add anyone from the directory, remove them), its channels (rename, delete) and its
 * settings (name, description, visibility, delete team). Owners can do all of it; other members see who is in the team.
 * Adding a person to the team adds them to every open channel (not to private ones); removing takes them out of all.
 */
export function ManageTeamDialog({ team, me, onClose, onDeleted }: {
    team: Team;
    me: string;
    onClose: () => void;
    onDeleted: () => void;
}) {
    // Escape closes the dialog wherever focus is.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);
    const token = useAccessToken();
    const qc = useQueryClient();
    const isOwner = normalizeUserId(team.ownerUserId) === me;
    const [tab, setTab] = useState<Tab>("people");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    const channelsQuery = useQuery({
        queryKey: ["channels", team.id, token],
        queryFn: () => connectApi.listChannels(team.id, token),
        enabled: Boolean(token)
    });
    const channels = useMemo(() => channelsQuery.data ?? [], [channelsQuery.data]);
    const memberQueries = useQueries({
        queries: channels.map((c) => ({
            queryKey: ["channel-members", team.id, c.id, token],
            queryFn: () => connectParityApi.listChannelMembers(team.id, c.id, token),
            enabled: Boolean(token)
        }))
    });
    const loadingPeople = channelsQuery.isPending || memberQueries.some((q) => q.isPending);

    /** Everyone in the team (in any channel), with the channels they are in. */
    const people = useMemo(() => {
        const byUser = new Map<string, { member: ChannelMember; rows: { channelId: string; memberId: string }[] }>();
        channels.forEach((c, i) => {
            for (const m of memberQueries[i]?.data ?? []) {
                const key = normalizeUserId(m.userId);
                const hit = byUser.get(key);
                if (hit) hit.rows.push({ channelId: c.id, memberId: m.id });
                else byUser.set(key, { member: m, rows: [{ channelId: c.id, memberId: m.id }] });
            }
        });
        return [...byUser.entries()]
            .map(([userId, v]) => ({ userId, ...v }))
            .sort((a, b) => (a.member.displayName ?? a.member.userEmail).localeCompare(b.member.displayName ?? b.member.userEmail));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channels, memberQueries.map((q) => q.dataUpdatedAt).join(",")]);

    const refresh = async () => {
        await qc.invalidateQueries({ queryKey: ["channel-members", team.id] });
        await qc.invalidateQueries({ queryKey: ["channels", team.id] });
        await qc.invalidateQueries({ queryKey: ["teams"] });
    };
    const run = async (fn: () => Promise<void>, failure: string) => {
        setBusy(true);
        setError(null);
        try {
            await fn();
            await refresh();
        } catch (err) {
            const msg = err instanceof Error ? err.message : "";
            setError(msg.includes("403") ? "Only the team's owner can do that." : failure);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={`Manage team ${team.name}`}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        >
            <div className="flex h-[min(34rem,92vh)] w-full max-w-2xl flex-col overflow-hidden rounded-lg border bg-card shadow-2xl">
                <header className="flex items-center justify-between border-b px-4 py-3">
                    <div className="min-w-0">
                        <h2 className="truncate text-sm font-semibold">Manage team: {team.name}</h2>
                        <p className="text-xs text-muted-foreground">{isOwner ? "You own this team." : "You are a member. Only the owner can make changes."}</p>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-accent"><X className="h-4 w-4" /></button>
                </header>
                <div role="tablist" className="flex gap-1 border-b px-3">
                    {([["people", "People"], ["channels", "Channels"], ["settings", "Settings"]] as const).map(([id, label]) => (
                        <button
                            key={id}
                            role="tab"
                            type="button"
                            aria-selected={tab === id}
                            onClick={() => { setTab(id); setError(null); }}
                            className={cn("border-b-2 px-3 py-2 text-sm", tab === id ? "border-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground")}
                        >
                            {label}
                        </button>
                    ))}
                </div>
                {error ? <p role="alert" className="border-b bg-destructive/10 px-4 py-2 text-xs text-destructive">{error}</p> : null}
                <div className="min-h-0 flex-1 overflow-y-auto p-4">
                    {tab === "people" ? (
                        <PeopleTab
                            people={people}
                            loading={loadingPeople}
                            channels={channels}
                            me={me}
                            ownerId={normalizeUserId(team.ownerUserId)}
                            isOwner={isOwner}
                            busy={busy}
                            onAdd={(u) => run(async () => {
                                const open = channels.filter((c) => c.kind !== "Private");
                                for (const c of open) {
                                    try {
                                        await connectParityApi.addChannelMember(team.id, c.id, { userId: normalizeUserId(u.userId), email: u.email, displayName: u.displayName }, token);
                                    } catch (e) {
                                        // Already in that channel is fine; anything else stops here.
                                        if (!(e instanceof Error && e.message.includes("400"))) throw e;
                                    }
                                }
                            }, "Couldn't add that person.")}
                            onRemove={(p) => run(async () => {
                                for (const r of p.rows) await connectParityApi.removeChannelMember(team.id, r.channelId, r.memberId, token);
                            }, "Couldn't remove that person.")}
                        />
                    ) : tab === "channels" ? (
                        <ChannelsTab team={team} channels={channels} isOwner={isOwner} busy={busy} run={run} token={token} />
                    ) : (
                        <SettingsTab team={team} isOwner={isOwner} busy={busy} run={run} token={token} onDeleted={onDeleted} />
                    )}
                </div>
            </div>
        </div>
    );
}

interface PersonRow { userId: string; member: ChannelMember; rows: { channelId: string; memberId: string }[] }

function PeopleTab({ people, loading, channels, me, ownerId, isOwner, busy, onAdd, onRemove }: {
    people: PersonRow[];
    loading: boolean;
    channels: Channel[];
    me: string;
    ownerId: string;
    isOwner: boolean;
    busy: boolean;
    onAdd: (u: { userId: string; email: string; displayName: string | null }) => void;
    onRemove: (p: PersonRow) => void;
}) {
    const token = useAccessToken();
    const [q, setQ] = useState("");
    const [debounced, setDebounced] = useState("");
    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(q.trim()), 250);
        return () => window.clearTimeout(t);
    }, [q]);
    const found = useQuery({
        queryKey: ["team-dir", debounced, token],
        queryFn: () => connectParityApi.searchDirectory(debounced, token),
        enabled: Boolean(token) && debounced.length >= 2 && isOwner
    });
    const inTeam = new Set(people.map((p) => p.userId));
    const results = (found.data ?? []).filter((u) => !inTeam.has(normalizeUserId(u.userId)));

    return (
        <div className="space-y-4">
            {isOwner ? (
                <div>
                    <label className="mb-1 block text-xs font-medium" htmlFor="team-add-person">Add people</label>
                    <div className="relative">
                        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                        <input
                            id="team-add-person"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search your organisation by name or email"
                            className="h-9 w-full rounded-md border bg-background pl-7 pr-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                        />
                    </div>
                    {results.length > 0 ? (
                        <ul className="mt-1 max-h-40 overflow-auto rounded-md border" aria-label="Search results">
                            {results.map((u) => (
                                <li key={u.userId}>
                                    <button
                                        type="button"
                                        disabled={busy}
                                        onClick={() => { onAdd(u); setQ(""); }}
                                        className="flex w-full items-center gap-2 px-2 py-1.5 text-left text-sm hover:bg-accent disabled:opacity-50"
                                    >
                                        <ChatAvatar name={u.displayName ?? u.email} size="sm" />
                                        <span className="min-w-0 flex-1"><span className="block truncate">{u.displayName ?? u.email}</span><span className="block truncate text-[11px] text-muted-foreground">{u.email}</span></span>
                                        <span className="text-xs font-medium text-primary">Add</span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : debounced.length >= 2 && !found.isFetching ? <p className="mt-1 text-xs text-muted-foreground">No one else matches.</p> : null}
                </div>
            ) : null}
            <div>
                <h3 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Members ({people.length})</h3>
                {loading ? <p className="text-sm text-muted-foreground"><Loader2 className="mr-1 inline h-4 w-4 animate-spin" />Loading…</p> : (
                    <ul className="divide-y rounded-md border">
                        {people.map((p) => {
                            const isTeamOwner = p.userId === ownerId;
                            return (
                                <li key={p.userId} className="flex items-center gap-2 px-2 py-1.5 text-sm">
                                    <ChatAvatar name={p.member.displayName ?? p.member.userEmail} size="sm" />
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate">{p.member.displayName ?? p.member.userEmail}{p.userId === me ? " (you)" : ""}</span>
                                        <span className="block truncate text-[11px] text-muted-foreground">{p.member.userEmail} · in {p.rows.length} of {channels.length} channels</span>
                                    </span>
                                    {isTeamOwner ? <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">Owner</span> : null}
                                    {isOwner && !isTeamOwner ? (
                                        <button type="button" disabled={busy} onClick={() => onRemove(p)} aria-label={`Remove ${p.member.displayName ?? p.member.userEmail}`} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive disabled:opacity-50"><X className="h-4 w-4" /></button>
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );
}

function ChannelsTab({ team, channels, isOwner, busy, run, token }: {
    team: Team;
    channels: Channel[];
    isOwner: boolean;
    busy: boolean;
    run: (fn: () => Promise<void>, failure: string) => Promise<void>;
    token: string | undefined;
}) {
    const [editing, setEditing] = useState<string | null>(null);
    const [name, setName] = useState("");
    const [desc, setDesc] = useState("");
    const [confirm, setConfirm] = useState<string | null>(null);

    return (
        <ul className="divide-y rounded-md border">
            {channels.map((c) => (
                <li key={c.id} className="px-3 py-2 text-sm">
                    {editing === c.id ? (
                        <div className="space-y-1.5">
                            <input value={name} onChange={(e) => setName(e.target.value)} aria-label="Channel name" disabled={c.isDefault} className="h-8 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-60" />
                            <input value={desc} onChange={(e) => setDesc(e.target.value)} aria-label="Channel description" placeholder="Description (optional)" className="h-8 w-full rounded-md border bg-background px-2 text-sm" />
                            <div className="flex gap-1.5">
                                <button type="button" disabled={busy || !name.trim()} onClick={() => void run(async () => { await connectParityApi.updateChannel(team.id, c.id, { name: name.trim().toLowerCase().replace(/\s+/g, "-"), description: desc.trim() || null }, token); setEditing(null); }, "Couldn't save the channel.")} className="rounded-md bg-primary px-3 py-1 text-xs font-medium text-primary-foreground disabled:opacity-50">Save</button>
                                <button type="button" onClick={() => setEditing(null)} className="rounded-md border px-3 py-1 text-xs">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2">
                            <Hash className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                            <span className="min-w-0 flex-1">
                                <span className="block truncate font-medium">{c.name}{c.isDefault ? " (General)" : ""}</span>
                                {c.description ? <span className="block truncate text-xs text-muted-foreground">{c.description}</span> : null}
                            </span>
                            {isOwner ? (
                                confirm === c.id ? (
                                    <span className="flex items-center gap-1 text-xs">
                                        Delete #{c.name}?
                                        <button type="button" disabled={busy} onClick={() => void run(async () => { await connectParityApi.deleteChannel(team.id, c.id, token); setConfirm(null); }, "Couldn't delete the channel.")} className="rounded bg-destructive px-2 py-0.5 font-medium text-destructive-foreground">Delete</button>
                                        <button type="button" onClick={() => setConfirm(null)} className="rounded border px-2 py-0.5">No</button>
                                    </span>
                                ) : (
                                    <>
                                        <button type="button" onClick={() => { setEditing(c.id); setName(c.name); setDesc(c.description ?? ""); }} className="rounded border px-2 py-0.5 text-xs hover:bg-accent">Edit</button>
                                        {!c.isDefault ? <button type="button" onClick={() => setConfirm(c.id)} aria-label={`Delete ${c.name}`} className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-destructive"><Trash2 className="h-4 w-4" /></button> : null}
                                    </>
                                )
                            ) : null}
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );
}

function SettingsTab({ team, isOwner, busy, run, token, onDeleted }: {
    team: Team;
    isOwner: boolean;
    busy: boolean;
    run: (fn: () => Promise<void>, failure: string) => Promise<void>;
    token: string | undefined;
    onDeleted: () => void;
}) {
    const [name, setName] = useState(team.name);
    const [description, setDescription] = useState(team.description ?? "");
    const [visibility, setVisibility] = useState(team.visibility);
    const [saved, setSaved] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    return (
        <div className="space-y-3">
            <div>
                <label htmlFor="team-name" className="mb-1 block text-xs font-medium">Team name</label>
                <input id="team-name" value={name} onChange={(e) => { setName(e.target.value); setSaved(false); }} disabled={!isOwner} className="h-9 w-full rounded-md border bg-background px-2 text-sm disabled:opacity-60" />
            </div>
            <div>
                <label htmlFor="team-desc" className="mb-1 block text-xs font-medium">Description</label>
                <textarea id="team-desc" value={description} onChange={(e) => { setDescription(e.target.value); setSaved(false); }} disabled={!isOwner} rows={3} className="w-full resize-none rounded-md border bg-background px-2 py-1.5 text-sm disabled:opacity-60" />
            </div>
            <div>
                <label htmlFor="team-vis" className="mb-1 block text-xs font-medium">Privacy</label>
                <select id="team-vis" value={visibility} onChange={(e) => { setVisibility(e.target.value); setSaved(false); }} disabled={!isOwner} className="h-9 rounded-md border bg-background px-2 text-sm disabled:opacity-60">
                    <option value="Private">Private: only people you add</option>
                    <option value="Public">Public: anyone in the organisation can find it</option>
                </select>
            </div>
            {isOwner ? (
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        disabled={busy || !name.trim()}
                        onClick={() => void run(async () => { await connectParityApi.updateTeam(team.id, { slug: team.slug, name: name.trim(), description: description.trim() || null, visibility }, token); setSaved(true); }, "Couldn't save the team.")}
                        className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
                    >
                        Save changes
                    </button>
                    {saved ? <span role="status" className="text-xs text-muted-foreground">Saved.</span> : null}
                </div>
            ) : null}
            {isOwner ? (
                <div className="mt-6 rounded-md border border-destructive/40 p-3">
                    <p className="text-sm font-medium">Delete this team</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">Removes the team, its channels and their members. Messages are no longer reachable.</p>
                    {confirmDelete ? (
                        <div className="mt-2 flex items-center gap-2 text-xs">
                            <span>Delete “{team.name}” for everyone?</span>
                            <button type="button" disabled={busy} onClick={() => void run(async () => { await connectApi.deleteTeam(team.id, token); onDeleted(); }, "Couldn't delete the team.")} className="rounded bg-destructive px-2 py-1 font-medium text-destructive-foreground">Delete team</button>
                            <button type="button" onClick={() => setConfirmDelete(false)} className="rounded border px-2 py-1">Cancel</button>
                        </div>
                    ) : (
                        <button type="button" onClick={() => setConfirmDelete(true)} className="mt-2 rounded-md border border-destructive px-3 py-1 text-xs font-medium text-destructive hover:bg-destructive/10">Delete team…</button>
                    )}
                </div>
            ) : null}
        </div>
    );
}
